import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import DataTable from '../../components/tables/DataTable';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import ViewTicketModal from '../../components/features/tickets/ViewTicketModal';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import engineerService from '../../services/engineerService';
import { formatDateTime } from '../../utils/dateFormatter';
import SignatureCanvas from 'react-signature-canvas';

const createVisitTimestamp = () => ({
  id: Date.now() + Math.floor(Math.random() * 1000),
  start_time: '',
  end_time: ''
});

const getBackendSignatureDate = (reportLike) => {
  if (!reportLike) return null;

  return (
    reportLike.signature_date ||
    reportLike.engineer_signature_date ||
    reportLike.signed_at ||
    reportLike.signature_timestamp ||
    reportLike.submitted_at ||
    null
  );
};

const WorkReportView = ({ prefilledTicket, onPrefillConsumed }) => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [reportableTickets, setReportableTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [editingReportStatus, setEditingReportStatus] = useState(null);
  const [hasExistingSignature, setHasExistingSignature] = useState(false);
  const [backendSignatureDate, setBackendSignatureDate] = useState(null);
  const { toasts, removeToast, success, error } = useToast();
  const signaturePadRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    ticket_id: '',
    work_status: 'COMPLETED',
    work_description: '',
    parts_used: '',
    visit_timestamps: [createVisitTimestamp()],
    photos: [],
    checklist: [],
    signature_data: null
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!prefilledTicket?.id || reportableTickets.length === 0) {
      return;
    }

    const prefilledTicketId = String(prefilledTicket.id);
    const ticketExists = reportableTickets.some((ticket) => String(ticket.id) === prefilledTicketId);

    if (!ticketExists) {
      error('Selected ticket is not available for work report');
      if (typeof onPrefillConsumed === 'function') {
        onPrefillConsumed();
      }
      return;
    }

    setFormData((prev) => ({ ...prev, ticket_id: prefilledTicketId }));
    success(`Ticket #${prefilledTicketId} selected for work report`);

    if (typeof onPrefillConsumed === 'function') {
      onPrefillConsumed();
    }
  }, [prefilledTicket, reportableTickets, onPrefillConsumed, success, error]);

  const loadData = async () => {
    setLoading(true);
    
    const reportsResult = await engineerService.getWorkReports(user.id);
    if (reportsResult.success) {
      setReports(reportsResult.data);
    }
    
    const ticketsResult = await engineerService.getReportableTickets(user.id);
    if (ticketsResult.success) {
      setReportableTickets(ticketsResult.data);
    }
    
    setLoading(false);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleVisitTimestampChange = (visitId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      visit_timestamps: prev.visit_timestamps.map((visit) =>
        visit.id === visitId ? { ...visit, [field]: value } : visit
      )
    }));
  };

  const handleAddVisitTimestamp = () => {
    setFormData((prev) => ({
      ...prev,
      visit_timestamps: [...prev.visit_timestamps, createVisitTimestamp()]
    }));
  };

  const handleDeleteVisitTimestamp = (visitId) => {
    setFormData((prev) => ({
      ...prev,
      visit_timestamps: prev.visit_timestamps.filter((visit) => visit.id !== visitId)
    }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setFormData(prev => ({ ...prev, photos: [...prev.photos, ...files] }));
    success(`${files.length} photo(s) uploaded!`);
    e.target.value = '';
  };

  const handleRemovePhotoFile = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      photos: (prev.photos || []).filter((_, index) => index !== indexToRemove)
    }));
    success('Photo removed');
  };

  const handleChecklistUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setFormData(prev => ({
      ...prev,
      checklist: [...(prev.checklist || []), ...files]
    }));
    success(`${files.length} checklist file(s) uploaded!`);
    e.target.value = '';
  };

  const handleRemoveChecklistFile = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      checklist: (prev.checklist || []).filter((_, index) => index !== indexToRemove)
    }));
    success('Checklist file removed');
  };

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
    setFormData(prev => ({ ...prev, signature_data: null }));
  };

  const saveSignature = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const signatureData = signaturePadRef.current.toDataURL();
      setFormData(prev => ({ ...prev, signature_data: signatureData }));
      success('Signature saved!');
      return signatureData;
    } else {
      error('Please provide a signature');
      return null;
    }
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      error('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const signatureData = event.target.result;
      setFormData(prev => ({ ...prev, signature_data: signatureData }));
      success('Signature image uploaded!');
      
      // If we have a pad, clear it so there's no confusion
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }
    };
    reader.readAsDataURL(file);
    e.target.value = ''; // Reset for next time
  };

  const handleShowPreview = () => {
    // Validation for preview
    if (!formData.ticket_id) {
      error('Please select a ticket');
      return;
    }
    if (!formData.work_description) {
      error('Please provide work description');
      return;
    }
    for (let index = 0; index < formData.visit_timestamps.length; index += 1) {
      const visit = formData.visit_timestamps[index];
      if (!visit.start_time || !visit.end_time) {
        error(`Please complete start and end time for Visit ${index + 1}`);
        return;
      }

      if (new Date(visit.end_time) <= new Date(visit.start_time)) {
        error(`Visit ${index + 1} end time must be after start time`);
        return;
      }
    }

    setShowPreviewModal(true);
  };

  const buildReportPayload = (isDraft, photo_keys = [], checklist_keys = []) => {
    const selectedTicket = getSelectedTicketInfo();
    const payload = {
      ticket_id: parseInt(formData.ticket_id, 10),
      action_taken: formData.work_description,
      resolution_status: formData.work_status,
      customer_name: selectedTicket?.site_name || user.name || 'N/A',
      timestamps: formData.visit_timestamps.map((visit) => ({
        time_in: visit.start_time ? new Date(visit.start_time).toISOString() : null,
        time_out: visit.end_time ? new Date(visit.end_time).toISOString() : null
      })),
      observations: formData.parts_used || null,
      engineer_id: user.id,
      is_draft: isDraft,
      status: isDraft ? 'DRAFT' : 'UNDER_REVIEW',
      photo_keys,
      checklist_keys
    };

    // Keep compatibility with backends still expecting a singular key.
    if (checklist_keys.length > 0) {
      payload.checklist_key = checklist_keys[0];
    }

    if (formData.signature_data) {
      payload.engineer_signature = formData.signature_data;
    }

    return payload;
  };

  const syncAttachments = async () => {
    try {
      const photo_keys = [];
      const checklist_keys = [];
      // Handle photos mapping
      const pendingPhotos = Array.isArray(formData.photos) ? formData.photos : Array.from(formData.photos || []);
      for (const photo of pendingPhotos) {
        if (photo instanceof File) {
          const res = await engineerService.uploadWorkReportAttachment(photo);
          if (res?.success && res.data?.s3_object_key) {
             photo_keys.push(res.data.s3_object_key);
          }
        }
      }

      // Handle checklist mapping (multiple files)
      const pendingChecklists = Array.isArray(formData.checklist) ? formData.checklist : [];
      for (const checklistFile of pendingChecklists) {
        if (checklistFile instanceof File) {
          const res = await engineerService.uploadWorkReportAttachment(checklistFile);
          if (res?.success && res.data?.s3_object_key) {
            checklist_keys.push(res.data.s3_object_key);
          }
        }
      }

      return { success: true, photo_keys, checklist_keys };
    } catch (err) {
      console.error('File Upload Integration Failed', err);
      error('Failed to sync all attachments. Please check network connection.');
      return { success: false };
    }
  };

  const handleSaveDraft = async () => {
    if (!formData.ticket_id) {
       error('Please select a ticket before saving a draft.');
       return;
    }

    const syncResult = await syncAttachments();
    if (!syncResult.success) return;

    const reportData = buildReportPayload(true, syncResult.photo_keys, syncResult.checklist_keys);

    let result;
    if (editingDraftId) {
      result = await engineerService.updateWorkReport(editingDraftId, reportData);
    } else {
      result = await engineerService.createWorkReport(reportData);
    }

    if (result.success) {
      setBackendSignatureDate(getBackendSignatureDate(result.data));
      success('Report saved as draft!');
      setShowPreviewModal(false);
      resetForm();
      loadData();
    } else {
      error(result.error || 'Failed to save draft');
    }
  };

  const handleSubmitReport = async () => {
    if (!formData.ticket_id) {
       error('Please select a ticket before submitting.');
       return;
    }

  const hasIncompleteVisit = formData.visit_timestamps.some((visit) => !visit.start_time || !visit.end_time);
    if (hasIncompleteVisit) {
      error('Please fill start date and end date for the visit');
      return;
    }

  const hasInvalidVisit = formData.visit_timestamps.some((visit) => new Date(visit.end_time) <= new Date(visit.start_time));
    if (hasInvalidVisit) {
      error('End time must be after start time');
      return;
    }
    
    // Validate signature for submission
    if (!formData.signature_data) {
      error('Please save your signature before submitting');
      return;
    }

    const syncResult = await syncAttachments();
    if (!syncResult.success) return;

    const reportData = buildReportPayload(false, syncResult.photo_keys, syncResult.checklist_keys);

    let result;
    if (editingDraftId) {
      result = await engineerService.updateWorkReport(editingDraftId, reportData);
    } else {
      result = await engineerService.createWorkReport(reportData);
    }

    if (result.success) {
      // Trigger the backend submit workflow transition DRAFT -> UNDER_REVIEW and TICKET -> PENDING
      const submitResult = await engineerService.submitWorkReport(result.data.id);
      
      if (submitResult.success) {
        setBackendSignatureDate(getBackendSignatureDate(submitResult.data) || getBackendSignatureDate(result.data));
        success('Report submitted successfully!');
        resetForm();
        setShowPreviewModal(false);
        loadData();
      } else {
        error(submitResult.error || 'Failed to finalize submission');
      }
    } else {
      error(result.error || 'Failed to submit report details');
    }
  };

  const handleSavePendingChanges = async () => {
    if (!formData.ticket_id) {
       error('Please select a ticket before saving.');
       return;
    }

    const hasIncompleteVisit = formData.visit_timestamps.some((visit) => !visit.start_time || !visit.end_time);
    if (hasIncompleteVisit) {
      error('Please fill start date and end date for the visit');
      return;
    }

    const hasInvalidVisit = formData.visit_timestamps.some((visit) => new Date(visit.end_time) <= new Date(visit.start_time));
    if (hasInvalidVisit) {
      error('End time must be after start time');
      return;
    }

    if (!formData.signature_data) {
      error('Please sign the pad and save your signature before updating');
      return;
    }

    const syncResult = await syncAttachments();
    if (!syncResult.success) return;

    const reportData = buildReportPayload(false, syncResult.photo_keys, syncResult.checklist_keys);

    const result = await engineerService.updateWorkReport(editingDraftId, reportData);

    if (result.success) {
      success('Work report updated successfully!');
      resetForm();
      setShowPreviewModal(false);
      loadData();
    } else {
      error(result.error || 'Failed to update work report');
    }
  };

  const resetForm = () => {
    setFormData({
      ticket_id: '',
      work_status: 'COMPLETED',
      work_description: '',
      parts_used: '',
      visit_timestamps: [createVisitTimestamp()],
      photos: [],
      checklist: [],
      signature_data: null
    });
    setEditingDraftId(null);
    setEditingReportStatus(null);
    setHasExistingSignature(false);
    setBackendSignatureDate(null);
    clearSignature();
  };

  const handleViewTicket = (report) => {
    if (report.ticket) {
      setSelectedTicket(report.ticket);
      setShowViewModal(true);
    }
  };

  const handleEditDraft = (report) => {
    setEditingDraftId(report.id);
    setEditingReportStatus(report.status);
    setHasExistingSignature(!!report.engineer_signature_s3_key);
    setBackendSignatureDate(getBackendSignatureDate(report));
    
    // Map timestamps back to visit format
    const mappedVisits = report.timestamps && report.timestamps.length > 0
      ? report.timestamps.map((ts, idx) => ({
          id: Date.now() + idx,
          start_time: ts.time_in ? ts.time_in.slice(0, 16) : '',
          end_time: ts.time_out ? ts.time_out.slice(0, 16) : ''
        }))
      : [createVisitTimestamp()];

    setFormData({
      ticket_id: String(report.ticket_id),
      work_status: report.resolution_status || 'COMPLETED',
      work_description: report.action_taken || '',
      parts_used: report.observations || '',
      visit_timestamps: mappedVisits,
      photos: [],
      checklist: [],
      signature_data: null
    });
    
    // Smooth scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    success(report.status === 'UNDER_REVIEW' ? 'Report loaded for editing' : 'Draft loaded for editing');
  };

  const getSelectedTicketInfo = () => {
    if (!formData.ticket_id) return null;
    return reportableTickets.find(t => t.id.toString() === formData.ticket_id);
  };

  // Styles
  const cardStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px'
  };

  const formGroupStyle = {
    marginBottom: '20px'
  };

  const labelStyle = {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    marginBottom: '8px',
    display: 'block'
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    background: 'var(--background)',
    color: 'var(--text-primary)',
    fontSize: '14px',
    fontFamily: 'inherit'
  };

  const uploadBoxStyle = {
    width: '100%',
    padding: '40px',
    borderRadius: '8px',
    border: '2px dashed var(--border)',
    background: 'var(--background)',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  };

  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px'
  };

  const modalContentStyle = {
    background: 'var(--surface)',
    borderRadius: '12px',
    maxWidth: '800px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
  };

  const modalHeaderStyle = {
    padding: '24px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    background: 'var(--surface)',
    zIndex: 10
  };

  const modalBodyStyle = {
    padding: '24px'
  };

  const modalFooterStyle = {
    padding: '24px',
    borderTop: '1px solid var(--border)',
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    background: 'var(--background)',
    position: 'sticky',
    bottom: 0
  };

  const previewSectionStyle = {
    marginBottom: '24px',
    padding: '16px',
    background: 'var(--background)',
    borderRadius: '8px',
    border: '1px solid var(--border)'
  };

  const previewLabelStyle = {
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    marginBottom: '8px',
    textTransform: 'uppercase'
  };

  const previewValueStyle = {
    fontSize: '14px',
    color: 'var(--text-primary)',
    lineHeight: '1.6'
  };

  const visitCardStyle = {
    padding: '16px',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    background: 'var(--background)',
    marginBottom: '12px'
  };

  const visitHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  };

  const badgeStyle = (status) => {
    const colors = {
      'DRAFT': { bg: '#6b728020', color: '#6b7280' },
      'UNDER_REVIEW': { bg: '#f59e0b20', color: '#f59e0b' },
      'APPROVED': { bg: '#10b98120', color: '#10b981' },
      'REJECTED': { bg: '#ef444420', color: '#ef4444' },
      'STALE': { bg: "#9CA3AF20", color: "#6B7280"}
    };
    const style = colors[status] || { bg: '#6b728020', color: '#6b7280' };
    
    return {
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      background: style.bg,
      color: style.color
    };
  };

  // Table columns
  const columns = [
    {
      key: 'ticket_id',
      label: 'Ticket ID',
      sortable: true,
      render: (value) => (
        <span style={{ fontWeight: '700', color: 'var(--primary)' }}>
          #{value}
        </span>
      )
    },
    {
      key: 'ticket',
      label: 'Title',
      sortable: true,
      render: (value) => value?.title || '-'
    },
    {
      key: 'resolution_status',
      label: 'Work Status',
      sortable: true,
      render: (value) => (value || '').replace('_', ' ')
    },
    {
      key: 'submitted_at',
      label: 'Submitted',
      sortable: true,
      render: (value) => formatDateTime(value)
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span style={badgeStyle(value)}>
          {(value || '').replace('_', ' ')}
        </span>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading work reports...
      </div>
    );
  }

  const selectedTicketInfo = getSelectedTicketInfo();

  return (
    <div>
      {/* Toast Notifications */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      {/* Report Form */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '20px' }}>
          Submit Work Report
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          {/* Ticket Selection */}
          <div style={formGroupStyle}>
            <label style={labelStyle}>Ticket Number</label>
            <select
              value={formData.ticket_id}
              onChange={(e) => handleFormChange('ticket_id', e.target.value)}
              style={inputStyle}
              disabled={!!editingDraftId}
            >
              <option value="">Select a ticket</option>
              {reportableTickets.map(ticket => (
                <option key={ticket.id} value={ticket.id}>
                  #{ticket.id} - {ticket.title}
                </option>
              ))}
            </select>
          </div>

          {/* Work Status */}
          <div style={formGroupStyle}>
            <label style={labelStyle}>Work Status</label>
            <select
              value={formData.work_status}
              onChange={(e) => handleFormChange('work_status', e.target.value)}
              style={inputStyle}
            >
              <option value="COMPLETED">Completed</option>
              <option value="PARTIALLY_COMPLETED">Partially Completed</option>
              <option value="PENDING_PARTS">Pending Parts</option>
              <option value="NEED_ESCALATION">Need Escalation</option>
            </select>
          </div>

          {/* Work Description */}
          <div style={{ ...formGroupStyle, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Work Description</label>
            <textarea
              value={formData.work_description}
              onChange={(e) => handleFormChange('work_description', e.target.value)}
              style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
              placeholder="Describe the work performed..."
            />
          </div>

          {/* Parts Used */}
          <div style={{ ...formGroupStyle, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Parts Used</label>
            <textarea
              value={formData.parts_used}
              onChange={(e) => handleFormChange('parts_used', e.target.value)}
              style={{ ...inputStyle, minHeight: '220px', resize: 'vertical' }}
              placeholder="List parts and quantities used..."
            />
          </div>

          {/* Visit Timestamps */}
          <div style={{ ...formGroupStyle, gridColumn: '1 / -1' }}>
            <div style={{ ...visitHeaderStyle, marginBottom: '16px' }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Visit Timestamps</label>
              <Button variant="secondary" onClick={handleAddVisitTimestamp}>
                + Add Visit
              </Button>
            </div>

            {formData.visit_timestamps.map((visit, index) => {
              return (
                <div key={visit.id} style={visitCardStyle}>
                  <div style={visitHeaderStyle}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                      Visit {index + 1}
                    </div>
                    <Button
                      variant="danger"
                      onClick={() => handleDeleteVisitTimestamp(visit.id)}
                      disabled={formData.visit_timestamps.length === 1}
                    >
                      Delete
                    </Button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                    <div>
                      <label style={labelStyle}>Visit Start</label>
                      <input
                        className="work-report-datetime-input"
                        type="datetime-local"
                        value={visit.start_time}
                        onChange={(e) => handleVisitTimestampChange(visit.id, 'start_time', e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Visit End</label>
                      <input
                        className="work-report-datetime-input"
                        type="datetime-local"
                        value={visit.end_time}
                        onChange={(e) => handleVisitTimestampChange(visit.id, 'end_time', e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Photo Documentation */}
          <div style={{ ...formGroupStyle, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Photo Documentation</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              style={{ display: 'none' }}
              id="photo-upload"
            />
            <div
              onClick={() => document.getElementById('photo-upload').click()}
              style={uploadBoxStyle}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--primary)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📷</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
                Click to upload photos
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Multiple files allowed.
              </div>
              {formData.photos.length > 0 && (
                <div style={{ fontSize: '12px', color: '#10b981', marginTop: '8px', fontWeight: '600' }}>
                  {formData.photos.length} photo(s) uploaded
                  <div style={{ marginTop: '8px', fontWeight: '500', color: 'var(--text-primary)' }}>
                    {formData.photos.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                          marginBottom: '6px',
                          padding: '6px 8px',
                          border: '1px solid var(--border)',
                          borderRadius: '6px',
                          background: 'var(--surface)'
                        }}
                      >
                        <span style={{ overflowWrap: 'anywhere' }}>- {file.name}</span>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRemovePhotoFile(index);
                          }}
                          style={{
                            border: '1px solid rgba(239, 68, 68, 0.4)',
                            background: 'rgba(239, 68, 68, 0.08)',
                            color: '#ef4444',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                            padding: '3px 8px',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Checklist Documentation */}
          <div style={{ ...formGroupStyle, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Checklist Documentation</label>
            <input
              type="file"
              accept="image/*,application/pdf"
              multiple
              onChange={handleChecklistUpload}
              style={{ display: 'none' }}
              id="checklist-upload"
            />
            <div
              onClick={() => document.getElementById('checklist-upload').click()}
              style={uploadBoxStyle}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--primary)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
                Click to upload checklist
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Multiple files allowed. Checklist images or PDF documentation.
              </div>
              {Array.isArray(formData.checklist) && formData.checklist.length > 0 && (
                <div style={{ fontSize: '12px', color: '#10b981', marginTop: '8px', fontWeight: '600' }}>
                  {formData.checklist.length} checklist file(s) uploaded
                  <div style={{ marginTop: '8px', fontWeight: '500', color: 'var(--text-primary)' }}>
                    {formData.checklist.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                          marginBottom: '6px',
                          padding: '6px 8px',
                          border: '1px solid var(--border)',
                          borderRadius: '6px',
                          background: 'var(--surface)'
                        }}
                      >
                        <span style={{ overflowWrap: 'anywhere' }}>- {file.name}</span>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRemoveChecklistFile(index);
                          }}
                          style={{
                            border: '1px solid rgba(239, 68, 68, 0.4)',
                            background: 'rgba(239, 68, 68, 0.08)',
                            color: '#ef4444',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                            padding: '3px 8px',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Engineer Signature */}
          <div style={{ ...formGroupStyle, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Engineer Signature</label>
            <div style={{
              width: '100%',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              background: '#f9fafb',
              overflow: 'hidden'
            }}>
              <SignatureCanvas
                ref={signaturePadRef}
                onBegin={() => {
                  if (formData.signature_data) {
                    setFormData(prev => ({ ...prev, signature_data: null }));
                  }
                }}
                canvasProps={{
                  style: {
                    width: '100%',
                    height: '200px',
                    touchAction: 'none',
                    cursor: 'crosshair'
                  }
                }}
                penColor="#000"
                velocityFilterWeight={0.7}
                minWidth={0.5}
                maxWidth={2.5}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
              <Button variant="primary" onClick={saveSignature}>
                Save Signature
              </Button>
              <Button variant="secondary" onClick={clearSignature}>
                Clear Signature
              </Button>
              <input
                type="file"
                accept="image/*"
                onChange={handleSignatureUpload}
                style={{ display: 'none' }}
                id="signature-upload"
              />
              <Button variant="secondary" onClick={() => document.getElementById('signature-upload').click()}>
                Upload Image
              </Button>
            </div>
            {formData.signature_data && (
              <div style={{ 
                marginTop: '12px', 
                padding: '12px', 
                border: '1px solid rgba(16, 185, 129, 0.2)', 
                borderRadius: '8px', 
                background: 'rgba(16, 185, 129, 0.05)' 
              }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#10b981', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>✓</span> Current Saved Signature:
                </div>
                <img 
                  src={formData.signature_data} 
                  alt="Current Signature" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '120px', 
                    height: 'auto', 
                    borderRadius: '4px', 
                    border: '1px solid var(--border)', 
                    background: 'white',
                    display: 'block'
                  }} 
                />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            {editingReportStatus === 'UNDER_REVIEW' ? (
              <>
                <Button variant="secondary" onClick={resetForm}>
                  Cancel Edit
                </Button>
                <Button variant="primary" onClick={handleSavePendingChanges}>
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={handleShowPreview}>
                  Preview & Save Draft
                </Button>
                <Button variant="primary" onClick={handleSubmitReport}>
                  Submit Report
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Recent Reports Table */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '20px' }}>
          My Recent Reports
        </h3>
        {reports.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
            No reports submitted yet
          </div>
        ) : (
          <DataTable
            data={reports}
            columns={columns}
            onRowClick={handleViewTicket}
            actions={[
              { label: 'View Ticket', onClick: handleViewTicket },
              { 
                label: 'Edit Draft', 
                onClick: handleEditDraft,
                condition: (report) => report.status === 'DRAFT' || report.status === 'REJECTED'
              },
              {
                label: 'Edit Report',
                onClick: handleEditDraft,
                condition: (report) => report.status === 'UNDER_REVIEW'
              }
            ]}
          />
        )}
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            {/* Modal Header */}
            <div style={modalHeaderStyle}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
                Work Report Preview
              </h2>
              <button
                onClick={() => setShowPreviewModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  padding: '0 8px'
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={modalBodyStyle}>
              {/* Ticket Info */}
              <div style={previewSectionStyle}>
                <div style={previewLabelStyle}>Ticket Information</div>
                <div style={previewValueStyle}>
                  <strong>#{selectedTicketInfo?.id}</strong> - {selectedTicketInfo?.title}
                  <br />
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {selectedTicketInfo?.site_name} | {selectedTicketInfo?.system_type}
                  </span>
                </div>
              </div>

              {/* Work Status */}
              <div style={previewSectionStyle}>
                <div style={previewLabelStyle}>Work Status</div>
                <div style={previewValueStyle}>{(formData.work_status || '').replace('_', ' ')}</div>
              </div>

              {/* Work Description */}
              <div style={previewSectionStyle}>
                <div style={previewLabelStyle}>Work Description</div>
                <div style={previewValueStyle}>{formData.work_description}</div>
              </div>

              <div style={previewSectionStyle}>
                <div style={previewLabelStyle}>Parts Used</div>
                <div style={previewValueStyle}>{formData.parts_used || 'None'}</div>
              </div>

              <div style={previewSectionStyle}>
                <div style={previewLabelStyle}>Visit Timestamps</div>
                <div style={previewValueStyle}>
                  {formData.visit_timestamps.map((visit, index) => {
                    return (
                      <div key={visit.id} style={{ marginBottom: '10px' }}>
                        <strong>Visit {index + 1}</strong>
                        <br />
                        Start: {visit.start_time ? formatDateTime(visit.start_time) : '-'}
                        <br />
                        End: {visit.end_time ? formatDateTime(visit.end_time) : '-'}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Photos */}
              <div style={previewSectionStyle}>
                <div style={previewLabelStyle}>Photos Attached</div>
                <div style={previewValueStyle}>
                  {formData.photos.length > 0 ? (
                    <div>
                      {formData.photos.map((photo, index) => (
                        <div key={index} style={{ fontSize: '13px', marginBottom: '4px' }}>
                          📷 {photo.name}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: 'var(--text-secondary)' }}>No photos attached</span>
                  )}
                </div>
              </div>

              {/* Checklist */}
              <div style={previewSectionStyle}>
                <div style={previewLabelStyle}>Checklist</div>
                <div style={previewValueStyle}>
                  {Array.isArray(formData.checklist) && formData.checklist.length > 0 ? (
                    <div>
                      {formData.checklist.map((file, index) => (
                        <div key={`${file.name}-${index}`}>📋 {file.name}</div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: 'var(--text-secondary)' }}>No checklist attached</span>
                  )}
                </div>
              </div>

              {/* Signature */}
              <div style={previewSectionStyle}>
                <div style={previewLabelStyle}>Engineer Signature</div>
                {formData.signature_data ? (
                  <div>
                    <img 
                      src={formData.signature_data} 
                      alt="Signature" 
                      style={{ 
                        maxWidth: '300px', 
                        height: 'auto', 
                        border: '1px solid var(--border)', 
                        borderRadius: '4px',
                        background: 'white'
                      }} 
                    />
                    <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                      Date of Signature: {backendSignatureDate ? formatDateTime(backendSignatureDate) : '-'}
                    </div>
                  </div>
                ) : (
                  <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                    No signature provided
                  </div>
                )}
              </div>

              {/* Engineer Info */}
              <div style={previewSectionStyle}>
                <div style={previewLabelStyle}>Submitted By</div>
                <div style={previewValueStyle}>
                  {user.name} {user.designation ? `(${user.designation})` : `(${user.role})`} - {user.email}
                  <br />
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {new Date().toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={modalFooterStyle}>
              <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
                Close
              </Button>
              <Button variant="primary" onClick={handleSaveDraft}>
                Save as Draft
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* View Ticket Modal */}
      {showViewModal && selectedTicket && (
        <ViewTicketModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedTicket(null);
          }}
          ticket={selectedTicket}
        />
      )}
    </div>
  );
};

export default WorkReportView;